import { GanttTask, DisplayRow } from '../../types/gantt.types'
import { useShipLedger } from '../../hooks/useShipLedger'
import { useShipVendorPhones } from '../../hooks/useShipVendorPhones'
import { ShipLedgerRow, shipVendorLabel, shipSideLabelPlacement } from '../../constants/ship.logic'
import { ShipBarSideLabel } from '../ship/ShipBarSideLabel'
import { PARTNER_STATUS_VIEW } from '../../constants/partnerStatus'
// 막대 옆 반장·자재파트너 라벨

export type GanttBarLabelsProps = {
  showCont: boolean
  sigOn: boolean
  task: GanttTask
  l: number
  startOff: number
  span: number
  showFloat: boolean
  hideBars: boolean
  row: DisplayRow
  barIdx: number
  getTaskSL: (task: GanttTask) => { s: number; l: number; rawL: number; }
  totalDays: number
  DW: number
  barTop: number
  showVendor: boolean
  ledgerQ: ReturnType<typeof useShipLedger>
  phonesQ: ReturnType<typeof useShipVendorPhones>
  shipRowsByTask: Map<number, ShipLedgerRow[]>
}

export function GanttBarLabels({
  showCont, sigOn, task, l, startOff, span, showFloat, hideBars, row, barIdx, getTaskSL, totalDays, DW,
  barTop, showVendor, ledgerQ, phonesQ, shipRowsByTask,
}: GanttBarLabelsProps) {
  return (
    <>
      {/* 반장 이름/연락처 (바 오른쪽, 공간 부족 시 왼쪽) — 대기일 바 위에 겹치지
          않도록 대기일만큼 오른쪽 기준점을 밀어내고, 같은 행에 바가 여러 개면
          (동일 공종 여러 구간) 다음/이전 바에 가려지지 않도록 그 경계 안에서만
          표시하며 양쪽 다 자리가 없으면 라벨을 생략한다. */}
      {showCont && !sigOn && l > 0 && (() => {
        const barEnd = startOff + span
        const floatEnd = showFloat && !hideBars && task.leadTime ? barEnd + task.leadTime : barEnd
        // SIF-1 자재파트너가 시공: 반장 이름/'미배정' 대신 '자재파트너 시공 · {업체명}'. 업체명은 발주 거래처(자재파트너) 첫 행.
        // 아니면 반장 이름이 있으면 이름/연락처, 배정 자체가 없으면 '미배정'(F-03). 배정은 됐는데 이름이 비어 있으면 다른 값으로 덮지 않고 생략한다.
        const constructVendorName = task.constructByVendor ? (shipRowsByTask.get(task.taskId)?.[0]?.vendorName ?? null) : null
        const label = task.constructByVendor
          ? '자재파트너 시공' + (constructVendorName ? ` · ${constructVendorName}` : '')
          : task.foremanName
            ? task.foremanName + (task.foremanContact ? '/' + task.foremanContact : '')
            : task.assigneePartnerId == null ? PARTNER_STATUS_VIEW.UNASSIGNED.label : null
        if (label === null) return null
        // 실측 대신 문자당 평균 폭으로 근사한 라벨 폭(px) — 한글이 섞여 있어 넉넉히 잡는다.
        const estWidth = label.length * 7.5 + 12

        const nextBar = row.bars[barIdx + 1]
        const prevBar = row.bars[barIdx - 1]
        const rightLimitPx = (nextBar ? getTaskSL(nextBar).s : totalDays) * DW
        const leftLimitPx  = prevBar ? (getTaskSL(prevBar).s + getTaskSL(prevBar).l) * DW : 0

        const rightGapPx = rightLimitPx - floatEnd * DW
        const leftGapPx  = startOff * DW - leftLimitPx

        const onRight = rightGapPx >= estWidth
        const onLeft  = !onRight && leftGapPx >= estWidth
        if (!onRight && !onLeft) return null

        return (
          <div style={{
            position:      'absolute',
            top:           barTop,
            ...(onRight
              ? { left: floatEnd * DW + 5 }
              : { left: startOff * DW - 5, transform: 'translateX(-100%)' }),
            height:        20,
            display:       'flex',
            alignItems:    'center',
            fontSize:      9.5,
            fontWeight:    600,
            color:         'var(--shell-on-surface-2)',
            whiteSpace:    'nowrap',
            pointerEvents: 'none',
            zIndex:        2,
          }}>
            {label}
          </div>
        )
      })()}

      {/* 자재파트너 라벨(거래처/전화, 바 오른쪽·공간 부족 시 왼쪽) — [자재파트너] 켜짐 + 원장·전화 조회 완료 + 활성 행 있음일 때만 */}
      {showVendor && !task.constructByVendor && !sigOn && l > 0 &&!ledgerQ.isLoading && !phonesQ.isLoading && !ledgerQ.isError && !phonesQ.isError && (() => {
        const vrows = shipRowsByTask.get(task.taskId)
        const first = vrows?.[0]
        if (!vrows || !first) return null
        const label = shipVendorLabel(vrows.map(r => ({ vendorName: r.vendorName, vendorPhone: r.vendorId != null ? (phonesQ.data?.get(r.vendorId) ?? null) : null })))
        if (!label) return null
        const barEnd = startOff + span
        const floatEnd = showFloat && !hideBars && task.leadTime ? barEnd + task.leadTime : barEnd
        const nextBar = row.bars[barIdx + 1]
        const prevBar = row.bars[barIdx - 1]
        const place = shipSideLabelPlacement({
          startOff, barEnd, floatEnd, estWidth: label.length * 7.5 + 12,
          rightLimitPx: (nextBar ? getTaskSL(nextBar).s : totalDays) * DW,
          leftLimitPx: prevBar ? (getTaskSL(prevBar).s + getTaskSL(prevBar).l) * DW : 0,
          DW,
        })
        if (!place) return null
        return <ShipBarSideLabel label={label} side={place} top={barTop} anchorPx={place === 'right' ? floatEnd * DW : startOff * DW} />
      })()}
    </>
  )
}
